// ONE GATE: the `chatboard-org-sharing` flag, mirrored onto /api/me (server.js). It offers
// no control for a share that would grant nobody anything while off — see server.js's own
// comment on `orgSharingEnabled` for why this is NOT the safety-critical gate: PUT/GET
// /api/forecasts/:id/shares answer regardless of it, and what it gates is whether granting
// a share DOES anything, since `viewerSubjects.js` collapses every OTHER viewer's subjects
// to `[]` while off.
//
// A per-browser distribution switch used to AND with this (shared with copy links, default
// on). It is gone: a debug toggle whose only effect was to hide a shipped control was one
// more way for a user to end up without the control and no way to find out why.
//
// THE FLAG IS NOT A SECURITY BOUNDARY, and here that matters MORE than it does for copy
// links. `chatboard-copy-links` gates all four copy routes server-side (`gateClosed` answers
// 404). `chatboard-org-sharing` gates nothing of the sort: PUT/GET
// /api/forecasts/:id/shares answer regardless, and the flag only collapses other viewers'
// subjects to `[]`. So this controls what is OFFERED, never what is permitted — hiding a
// button has never been a gate, and on this surface the flag underneath is not one either.
// Do not read it as protection.
//
// Each surface keeps its OWN flag, which is what makes the scope selective: copy-links flag
// off + org-sharing flag on shows the share dialog and no copy controls.
//
// FLAG OFF MEANS ABSENT, NOT OFFERED-AND-REFUSED (checklist §4, the composer
// ruling extended here): the dialog must never render and then fail on
// submit. `isOpen` is DERIVED from `available` (same shape as
// useCopyLinksGate's `sendOpen`), so it can never outlive the gate — a flag
// that flips off while the dialog is open closes it on the next render,
// exactly like the copy-link modal.
import { useCallback, useState } from "react";

/**
 * Gate the share dialog, and own its open/request state.
 *
 * @param {object} args
 * @param {unknown} [args.orgSharingEnabled] `/api/me`'s `orgSharingEnabled`.
 *   Anything other than boolean `true` is off (fail closed).
 * @returns {{available: boolean, isOpen: boolean, target: unknown,
 *   open: (target?: unknown) => void, close: () => void}}
 */
export function useShareDialogGate({ orgSharingEnabled }) {
  // Boxed, so a request carrying NO target is still distinguishable from "not
  // open" — `null` is a legitimate target.
  const [requested, setRequested] = useState(null);
  // Strict `=== true`, matching useCopyLinksGate: a missing `orgSharingEnabled` (an older
  // /api/me, a failed flag eval, a 401 body) fails CLOSED. Sharing is on wherever the
  // deployment flag allows it; there is no second switch.
  const available = orgSharingEnabled === true;

  const open = useCallback(
    (target = null) => {
      if (available) setRequested({ target });
    },
    [available],
  );
  const close = useCallback(() => setRequested(null), []);

  return {
    available,
    isOpen: available && requested !== null,
    // The forecast the dialog was opened FOR — supplied by the opener, never
    // re-derived from the focused forecast, which its callers' rows need not be.
    target: available && requested ? requested.target : null,
    open,
    close,
  };
}
